/**
 * Liquid Cooling CDU edit form body — single "CDU" details card (#996 Phase 3).
 * Hand-written for the 6 per-instance CDU fields (NOT a mechanical mirror of the
 * cooling field set). Every field is wrapped in PermissionAwareField for RBAC.
 *
 * The per-CDU `lcAdditionalInformationSfn` Notes card renders here via
 * {@link RepeatableInstanceAdditionalInfo} when editing a CDU; its attachments
 * are scoped per-CDU (`LIQUID_COOLING_NOTES.unit.{id}`) as of #997 B1 / PD-2
 * (#1154 / #996).
 */

import React from 'react';
import { Platform, StyleSheet } from 'react-native';

import {
  LIQUID_COOLING_CDU_ADDITIONAL_INFORMATION_LABEL,
  LIQUID_COOLING_CDU_ADDITIONAL_INFORMATION_PLACEHOLDER,
  LIQUID_COOLING_CDU_ADDITIONAL_INFORMATION_SUBTITLE,
  LIQUID_COOLING_CDU_EDIT_DETAILS_CARD_SUBTITLE,
  LIQUID_COOLING_CDU_EDIT_GATE_CARD_SUBTITLE,
  LIQUID_COOLING_CDU_EDIT_STATUS_EXISTING_LABEL,
  LIQUID_COOLING_CDU_EDIT_TYPE_LABEL,
  LIQUID_COOLING_CDU_EDIT_TYPE_OPTIONS,
} from '../../constants/liquidCoolingCduConfigurationsSubsectionCopy';
import {
  LIQUID_COOLING_CDU_PLACEHOLDERS,
  LIQUID_COOLING_CDU_RACK_OR_ROW_OPTIONS,
} from '../../constants/liquidCoolingCduRequirementsUi';
import { REDUNDANCY_OPTIONS } from '../../constants/redundancy';
import {
  LIQUID_COOLING_CDU_EDITOR_SUBSECTION_FIELDS,
  LIQUID_COOLING_NOTES_SUBSECTION_FIELDS,
} from '../../constants/requestFieldDescriptors';
import { SECTION_IDS, getSectionLabel } from '../../constants/sectionIds';
import { useFormFieldVisibilityContext } from '../../hooks/useFormFieldVisibilityContext';
import { spacing } from '../../theme';
import {
  resolveRepeatableInstanceAttachmentSectionId,
  selectAttachmentsForRepeatableInstance,
  selectSelectedFilesForRepeatableInstance,
} from '../../utils/repeatableInstanceAttachmentSections';
import { subsectionHeaderMetricsProps } from '../../utils/subsectionHeaderMetrics';
import { booleanToYesNo, yesNoToBoolean } from '../../utils/yesNoBoolean';
import {
  FormRow,
  FormColumn,
  FormInput,
  FormDropdown,
  FormYesNoChoice,
  FormTwoOptionChoice,
  formFieldInStack,
  FormStackColumnsOnWebProvider,
  StepForm,
} from '../forms';
import { LocationTagPicker } from '../locationTags/LocationTagPicker';
import { PermissionAwareField } from '../PermissionAwareField';
import { RepeatableInstanceAdditionalInfo } from '../repeatableSections/repeatableInstanceAdditionalInfo';

import type { SavedAttachment } from '../../services/attachmentService';
import type {
  LiquidCoolingCduStatus,
  LiquidCoolingCduConfiguration,
} from '../../types/liquidCoolingCdu';
import type { SelectedFile } from '../forms/FileUpload';

export interface LiquidCoolingCduEditFormAttachmentProps {
  attachmentsBySection?: Record<string, SavedAttachment[]>;
  onAttachFiles?: (sectionId: string) => void;
  onDeleteAttachment?: (attachmentId: number) => void;
  selectedFiles?: Record<string, SelectedFile[]>;
  onFilesSelected?: (sectionId: string, files: SelectedFile[]) => void;
  isOffline?: boolean;
  onCaptionChange?: (attachmentId: number, caption: string) => void;
  pendingCaptionEdits?: Record<number, string>;
}

export interface LiquidCoolingCduEditFormContentProps
  extends LiquidCoolingCduEditFormAttachmentProps {
  formData: Partial<LiquidCoolingCduConfiguration>;
  updateField: <K extends keyof LiquidCoolingCduConfiguration>(
    field: K,
    value: LiquidCoolingCduConfiguration[K],
  ) => void;
  disabled?: boolean;
  requestStage?: string;
  requestId?: string;
  testID?: string;
  showAdditionalInformation?: boolean;
  sharedRequestFields?: Record<string, unknown>;
  onSharedRequestFieldChange?: (field: string, value: string | boolean) => void;
  onSharedFieldDirty?: () => void;
  showFullDetails?: boolean;
}

function liquidCoolingCduStatusToTypeLabel(
  status: LiquidCoolingCduStatus | undefined,
): string {
  return status === 'existing'
    ? LIQUID_COOLING_CDU_EDIT_TYPE_OPTIONS[0]
    : LIQUID_COOLING_CDU_EDIT_TYPE_OPTIONS[1];
}

function liquidCoolingCduTypeLabelToStatus(
  label: string,
): LiquidCoolingCduStatus {
  return label === LIQUID_COOLING_CDU_EDIT_STATUS_EXISTING_LABEL
    ? 'existing'
    : 'new';
}

function LiquidCoolingCduPermissionField({
  fieldId,
  requestStage,
  requestId,
  showFullDetails = true,
  children,
}: {
  fieldId: string;
  requestStage?: string;
  requestId?: string;
  showFullDetails?: boolean;
  children: React.ReactNode;
}) {
  if (!requestStage) {
    return <>{children}</>;
  }
  // Name/Type are unmapped identity fields. In Basic view the unmapped-field branch
  // in PermissionAwareField would return null and hide them (#997 Bug 2/7). Force them
  // always-visible (read-only in view mode via the form's `disabled` prop), matching
  // the other five repeatable sections, which don't forward showFullDetails.
  const identityField = fieldId === 'name' || fieldId === 'status';
  return (
    <PermissionAwareField
      fieldId={fieldId}
      requestStage={requestStage}
      requestId={requestId}
      showFullDetails={identityField || showFullDetails}
      containerStyle={formFieldInStack}
    >
      {children}
    </PermissionAwareField>
  );
}

const LIQUID_COOLING_CDU_EDITOR_SUBSECTION_PERSIST = {
  persistSectionExpanded: true,
} as const;

export const LiquidCoolingCduEditFormContent: React.FC<
  LiquidCoolingCduEditFormContentProps
> = ({
  formData,
  updateField,
  disabled = false,
  requestStage,
  requestId,
  attachmentsBySection,
  onAttachFiles,
  onDeleteAttachment,
  selectedFiles,
  onFilesSelected,
  isOffline = false,
  onCaptionChange,
  pendingCaptionEdits,
  testID = 'liquid-cooling-cdu-edit-form',
  showAdditionalInformation = false,
  sharedRequestFields = {},
  onSharedRequestFieldChange,
  showFullDetails = true,
}) => {
  const permissionStage = requestStage ?? 'DRAFT';
  const fieldVisibility = useFormFieldVisibilityContext({
    showFullDetails,
    requestStage: permissionStage,
    requestId,
  });
  const headerValues = {
    ...formData,
    lcLiquidToChipDesired: sharedRequestFields.lcLiquidToChipDesired,
  } as Record<string, unknown>;
  const cduMetrics = subsectionHeaderMetricsProps(
    LIQUID_COOLING_CDU_EDITOR_SUBSECTION_FIELDS,
    headerValues,
    fieldVisibility,
  );
  const attachmentsEnabled = true;
  const configurationId = formData.id;
  const detailsBaseSectionId = SECTION_IDS.LIQUID_COOLING_PROJECT;
  const detailsSectionId = resolveRepeatableInstanceAttachmentSectionId(
    detailsBaseSectionId,
    configurationId,
  );
  const detailsAttachments = selectAttachmentsForRepeatableInstance(
    attachmentsBySection ?? {},
    detailsBaseSectionId,
    configurationId,
  );
  const detailsSelectedFiles = selectSelectedFilesForRepeatableInstance(
    selectedFiles ?? {},
    detailsBaseSectionId,
    configurationId,
  );
  // #997 B1 / PD-2: scope the Notes (Additional-Info) attachments per-CDU, matching
  // the details card above and the LU/PD/Cooling Obs-7 fix. `RepeatableInstanceAdditionalInfo`
  // indexes `attachmentsBySection`/`selectedFiles` by the passed sectionId, so the full
  // maps flow through unchanged; only the id becomes unit-scoped.
  const notesSectionId = resolveRepeatableInstanceAttachmentSectionId(
    SECTION_IDS.LIQUID_COOLING_NOTES,
    configurationId,
  );
  return (
    <FormStackColumnsOnWebProvider enabled={Platform.OS === 'web'}>
      <StepForm
        title={getSectionLabel(detailsBaseSectionId)}
        titleSubtitle={LIQUID_COOLING_CDU_EDIT_GATE_CARD_SUBTITLE}
        defaultSectionExpanded
        {...LIQUID_COOLING_CDU_EDITOR_SUBSECTION_PERSIST}
        {...cduMetrics}
        sectionId={detailsSectionId}
        requestId={requestId}
        requestStage={permissionStage}
        allowAttachments={attachmentsEnabled}
        attachments={detailsAttachments}
        onAttachFiles={
          onAttachFiles ? () => onAttachFiles(detailsSectionId) : undefined
        }
        onDeleteAttachment={onDeleteAttachment}
        selectedFiles={detailsSelectedFiles}
        onFilesSelected={
          onFilesSelected
            ? files => onFilesSelected(detailsSectionId, files)
            : undefined
        }
        isOffline={isOffline}
        onCaptionChange={onCaptionChange}
        pendingCaptionEdits={pendingCaptionEdits}
        isEditing={!disabled}
        disabled={disabled}
        containerStyle={styles.sectionCard}
      >
        <FormRow>
          <FormColumn>
            <LiquidCoolingCduPermissionField
              fieldId="lcLiquidToChipDesired"
              requestStage={requestStage}
              requestId={requestId}
              showFullDetails={showFullDetails}
            >
              <FormYesNoChoice
                label="Is liquid-to-chip cooling desired?"
                value={booleanToYesNo(
                  sharedRequestFields.lcLiquidToChipDesired as
                    | boolean
                    | undefined,
                )}
                onSelect={
                  onSharedRequestFieldChange
                    ? value =>
                        onSharedRequestFieldChange(
                          'lcLiquidToChipDesired',
                          yesNoToBoolean(value),
                        )
                    : undefined
                }
                disabled={disabled}
                containerStyle={formFieldInStack}
              />
            </LiquidCoolingCduPermissionField>
          </FormColumn>
        </FormRow>
        <FormRow>
          <FormColumn>
            <LiquidCoolingCduPermissionField
              fieldId="name"
              requestStage={requestStage}
              requestId={requestId}
              showFullDetails={showFullDetails}
            >
              <FormInput
                label="Name"
                value={formData.name || ''}
                onChangeText={value => updateField('name', value)}
                placeholder="Enter a name for this liquid cooling unit (e.g., LC A)"
                disabled={disabled}
                testID={`${testID}-name`}
                containerStyle={formFieldInStack}
              />
            </LiquidCoolingCduPermissionField>
          </FormColumn>
          <FormColumn>
            <LiquidCoolingCduPermissionField
              fieldId="status"
              requestStage={requestStage}
              requestId={requestId}
              showFullDetails={showFullDetails}
            >
              <FormTwoOptionChoice
                label={LIQUID_COOLING_CDU_EDIT_TYPE_LABEL}
                value={liquidCoolingCduStatusToTypeLabel(formData.status)}
                options={[
                  LIQUID_COOLING_CDU_EDIT_TYPE_OPTIONS[0],
                  LIQUID_COOLING_CDU_EDIT_TYPE_OPTIONS[1],
                ]}
                onSelect={label =>
                  updateField(
                    'status',
                    liquidCoolingCduTypeLabelToStatus(label),
                  )
                }
                disabled={disabled}
                inline
                testID={`${testID}-type`}
                containerStyle={formFieldInStack}
              />
            </LiquidCoolingCduPermissionField>
          </FormColumn>
        </FormRow>

        <FormRow>
          <FormColumn>
            <LiquidCoolingCduPermissionField
              fieldId="locationTagId"
              requestStage={requestStage}
              requestId={requestId}
              showFullDetails={showFullDetails}
            >
              <LocationTagPicker
                requestId={requestId}
                requestStage={requestStage}
                value={formData.locationTagId ?? null}
                onChange={tagId => updateField('locationTagId', tagId)}
                disabled={disabled}
                testID={`${testID}-location-tag`}
                containerStyle={formFieldInStack}
              />
            </LiquidCoolingCduPermissionField>
          </FormColumn>
        </FormRow>

        <FormRow>
          <FormColumn>
            <LiquidCoolingCduPermissionField
              fieldId="lcDescription"
              requestStage={requestStage}
              requestId={requestId}
              showFullDetails={showFullDetails}
            >
              <FormInput
                label="Description"
                value={formData.lcDescription || ''}
                onChangeText={value => updateField('lcDescription', value)}
                placeholder={LIQUID_COOLING_CDU_PLACEHOLDERS.description}
                multiline
                maxLength={10000}
                richText
                requestId={requestId}
                requestSection={detailsSectionId}
                isOnline={!isOffline}
                disabled={disabled}
                containerStyle={formFieldInStack}
              />
            </LiquidCoolingCduPermissionField>
          </FormColumn>
          <FormColumn>
            <LiquidCoolingCduPermissionField
              fieldId="lcCduType"
              requestStage={requestStage}
              requestId={requestId}
              showFullDetails={showFullDetails}
            >
              <FormDropdown
                label="Rack or Row CDUs"
                value={formData.lcCduType || ''}
                options={[...LIQUID_COOLING_CDU_RACK_OR_ROW_OPTIONS]}
                onSelect={value =>
                  updateField(
                    'lcCduType',
                    Array.isArray(value) ? value[0] : value,
                  )
                }
                placeholder={LIQUID_COOLING_CDU_PLACEHOLDERS.cduType}
                disabled={disabled}
                testID={`${testID}-cdu-type`}
              />
            </LiquidCoolingCduPermissionField>
          </FormColumn>
        </FormRow>

        <FormRow>
          <FormColumn>
            <LiquidCoolingCduPermissionField
              fieldId="lcVertivSuppliesRowManifolds"
              requestStage={requestStage}
              requestId={requestId}
              showFullDetails={showFullDetails}
            >
              <FormYesNoChoice
                label="Does Vertiv supply row manifolds (row CDUs)?"
                value={booleanToYesNo(formData.lcVertivSuppliesRowManifolds)}
                onSelect={v =>
                  updateField('lcVertivSuppliesRowManifolds', v === 'Yes')
                }
                disabled={disabled}
                inline
                testID={`${testID}-row-manifolds`}
                containerStyle={formFieldInStack}
              />
            </LiquidCoolingCduPermissionField>
          </FormColumn>
          <FormColumn>
            <LiquidCoolingCduPermissionField
              fieldId="lcVertivSuppliesRackManifolds"
              requestStage={requestStage}
              requestId={requestId}
              showFullDetails={showFullDetails}
            >
              <FormYesNoChoice
                label="Does Vertiv supply rack manifolds (rack CDUs)?"
                value={booleanToYesNo(formData.lcVertivSuppliesRackManifolds)}
                onSelect={v =>
                  updateField('lcVertivSuppliesRackManifolds', v === 'Yes')
                }
                disabled={disabled}
                inline
                testID={`${testID}-rack-manifolds`}
                containerStyle={formFieldInStack}
              />
            </LiquidCoolingCduPermissionField>
          </FormColumn>
        </FormRow>

        <FormRow>
          <FormColumn>
            <LiquidCoolingCduPermissionField
              fieldId="lcCduRedundancy"
              requestStage={requestStage}
              requestId={requestId}
              showFullDetails={showFullDetails}
            >
              <FormDropdown
                label="CDU redundancy"
                value={formData.lcCduRedundancy || ''}
                options={[...REDUNDANCY_OPTIONS]}
                onSelect={value =>
                  updateField(
                    'lcCduRedundancy',
                    Array.isArray(value) ? value[0] : value,
                  )
                }
                placeholder={LIQUID_COOLING_CDU_PLACEHOLDERS.cduRedundancy}
                disabled={disabled}
                testID={`${testID}-redundancy`}
              />
            </LiquidCoolingCduPermissionField>
          </FormColumn>
          <FormColumn>
            <LiquidCoolingCduPermissionField
              fieldId="lcLeakDetectionApproach"
              requestStage={requestStage}
              requestId={requestId}
              showFullDetails={showFullDetails}
            >
              <FormInput
                label="Leak detection control approach"
                value={formData.lcLeakDetectionApproach || ''}
                onChangeText={value =>
                  updateField('lcLeakDetectionApproach', value)
                }
                placeholder={
                  LIQUID_COOLING_CDU_PLACEHOLDERS.leakDetectionApproach
                }
                maxLength={500}
                disabled={disabled}
                containerStyle={formFieldInStack}
              />
            </LiquidCoolingCduPermissionField>
          </FormColumn>
        </FormRow>
      </StepForm>

      {showAdditionalInformation ? (
        <RepeatableInstanceAdditionalInfo
          titleSubtitle={LIQUID_COOLING_CDU_ADDITIONAL_INFORMATION_SUBTITLE}
          sectionId={notesSectionId}
          fieldId="lcAdditionalInformationSfn"
          inputLabel={LIQUID_COOLING_CDU_ADDITIONAL_INFORMATION_LABEL}
          placeholder={LIQUID_COOLING_CDU_ADDITIONAL_INFORMATION_PLACEHOLDER}
          value={String(formData.lcAdditionalInformationSfn ?? '')}
          onChangeText={value =>
            updateField('lcAdditionalInformationSfn', value)
          }
          requestId={requestId}
          requestStage={permissionStage}
          disabled={disabled}
          subsectionFields={LIQUID_COOLING_NOTES_SUBSECTION_FIELDS}
          headerValues={formData as Record<string, unknown>}
          attachmentsBySection={attachmentsBySection ?? {}}
          onAttachFiles={onAttachFiles}
          onDeleteAttachment={onDeleteAttachment}
          selectedFiles={selectedFiles ?? {}}
          onFilesSelected={onFilesSelected}
          offlineMode={Boolean(isOffline)}
          onCaptionChange={onCaptionChange}
          pendingCaptionEdits={pendingCaptionEdits}
          showFullDetails={showFullDetails}
        />
      ) : null}
    </FormStackColumnsOnWebProvider>
  );
};

const styles = StyleSheet.create({
  sectionCard: {
    marginBottom: spacing.md,
  },
});
